import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import axios from '../../api/axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Вспомогательная функция для безопасного форматирования даты
const safeFormatDate = (date, formatStr = 'dd.MM.yyyy') => {
  try {
    if (!date || date === '' || date === null || date === undefined) return 'Дата не указана';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Дата не указана';
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Ошибка форматирования даты:', error, date);
    return 'Дата не указана';
  }
};

// Полный URL для изображения
const getFullImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  
  // Если путь начинается с /uploads, используем его как есть (относительный путь)
  // Браузер автоматически добавит текущий origin
  if (url.startsWith('/uploads')) {
    return url;
  }
  
  // Если путь начинается с uploads без слеша, добавляем слеш
  if (url.startsWith('uploads')) {
    return '/' + url;
  }
  
  // Если путь начинается с /, используем как есть
  if (url.startsWith('/')) {
    return url;
  }
  
  // Иначе возвращаем как есть
  return url;
};

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    date: '',
    time: '',
    location: '',
    category: 'other',
    mediaType: 'image',
    image: '',
    videoUrl: '',
    gallery: [],
    speakerName: '',
    capacity: '',
    published: true,
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      // Фильтруем и валидируем данные перед установкой
      const validatedEvents = (response.data || []).map(event => ({
        ...event,
        // Убеждаемся, что date либо валидна, либо null
        date: event.date && !isNaN(new Date(event.date).getTime()) ? event.date : null,
      }));
      setEvents(validatedEvents);
    } catch (error) {
      console.error('Ошибка загрузки мероприятий:', error);
      // Устанавливаем пустой массив при ошибке, чтобы не ломать интерфейс
      setEvents([]);
    }
  };

  const handleOpen = (event = null) => {
    if (event) {
      setEditingId(event._id);
      setFormData({
        ...event,
        date: event.date && !isNaN(new Date(event.date).getTime()) 
          ? format(new Date(event.date), 'yyyy-MM-dd') 
          : '',
        gallery: event.gallery || [],
      });
    } else {
      setEditingId(null);
      setFormData({
        description: '',
        date: '',
        time: '',
        location: '',
        category: 'other',
        mediaType: 'image',
        image: '',
        videoUrl: '',
        gallery: [],
        speakerName: '',
        capacity: '',
        published: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleFileUpload = async (e, fieldName = 'image', isGallery = false) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (isGallery) {
        setFormData({
          ...formData,
          gallery: [...formData.gallery, response.data.path],
        });
      } else {
        setFormData({
          ...formData,
          [fieldName]: response.data.path,
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      alert('Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = formData.gallery.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      gallery: newGallery,
    });
  };

  // Функция для проверки, не пустой ли HTML контент
  const isHtmlEmpty = (html) => {
    if (!html) return true;
    // Удаляем все HTML теги и проверяем, остался ли текст
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '' || text === '<p><br></p>' || text === '<p></p>';
  };

  const handleSubmit = async () => {
    // Валидация
    if (isHtmlEmpty(formData.description)) {
      alert('Введите описание мероприятия');
      return;
    }
    
    try {
      // Убираем title и пустые значения из данных перед отправкой
      const { title, ...dataWithoutTitle } = formData;
      
      // Убираем пустые строки и null значения
      const cleanedData = Object.fromEntries(
        Object.entries(dataWithoutTitle).filter(([_, value]) => {
          if (value === '' || value === null || value === undefined) return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        })
      );
      
      if (editingId) {
        await axios.put(`/events/${editingId}`, cleanedData);
      } else {
        await axios.post('/events', cleanedData);
      }
      fetchEvents();
      handleClose();
    } catch (error) {
      console.error('Ошибка сохранения мероприятия:', error);
      alert(`Ошибка сохранения мероприятия: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить это мероприятие?')) {
      try {
        await axios.delete(`/events/${id}`);
        fetchEvents();
      } catch (error) {
        console.error('Ошибка удаления мероприятия:', error);
      }
    }
  };

  const getMediaTypeLabel = (type) => {
    switch (type) {
      case 'image': return 'Изображение';
      case 'video': return 'Видео';
      case 'gallery': return 'Галерея';
      default: return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Управление мероприятиями</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить мероприятие
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Описание</TableCell>
              <TableCell>Дата</TableCell>
              <TableCell>Тип медиа</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Опубликовано</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => {
              try {
                return (
                  <TableRow key={event._id}>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography variant="body2" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {event.description 
                          ? event.description.replace(/<[^>]*>/g, '').substring(0, 100) + (event.description.replace(/<[^>]*>/g, '').length > 100 ? '...' : '')
                          : 'Без описания'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {safeFormatDate(event.date)}
                      {event.time && ` ${event.time}`}
                    </TableCell>
                    <TableCell>{getMediaTypeLabel(event.mediaType)}</TableCell>
                    <TableCell>{event.category}</TableCell>
                    <TableCell>{event.published ? 'Да' : 'Нет'}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleOpen(event)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(event._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              } catch (error) {
                console.error('Ошибка рендеринга мероприятия:', error, event);
                return null;
              }
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать мероприятие' : 'Добавить мероприятие'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Описание <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box
              sx={{
                '& .ql-editor': {
                  minHeight: '300px',
                  fontSize: '14px',
                },
                '& .ql-container': {
                  fontSize: '14px',
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(value) => {
                  setFormData({
                    ...formData,
                    description: value,
                  });
                }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'script': 'sub'}, { 'script': 'super' }],
                    [{ 'indent': '-1'}, { 'indent': '+1' }],
                    [{ 'direction': 'rtl' }],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'font': [] }],
                    [{ 'align': [] }],
                    ['clean'],
                    ['link', 'image', 'video'],
                    ['blockquote', 'code-block'],
                  ],
                }}
                formats={[
                  'header', 'font', 'size',
                  'bold', 'italic', 'underline', 'strike', 'blockquote',
                  'list', 'bullet', 'indent',
                  'link', 'image', 'video',
                  'color', 'background',
                  'align', 'script',
                  'code-block',
                ]}
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            label="Дата"
            name="date"
            type="date"
            value={formData.date || ''}
            onChange={handleChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Время"
            name="time"
            value={formData.time}
            onChange={handleChange}
            margin="normal"
            placeholder="например, 14:00"
          />
          <TextField
            fullWidth
            label="Место проведения"
            name="location"
            value={formData.location}
            onChange={handleChange}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Категория</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label="Категория"
            >
              <MenuItem value="consultation">Консультация</MenuItem>
              <MenuItem value="webinar">Вебинар</MenuItem>
              <MenuItem value="conference">Конференция</MenuItem>
              <MenuItem value="other">Другое</MenuItem>
            </Select>
          </FormControl>
          
          {/* Тип медиа */}
          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">Тип медиа-контента</FormLabel>
            <RadioGroup
              row
              name="mediaType"
              value={formData.mediaType}
              onChange={handleChange}
            >
              <FormControlLabel value="image" control={<Radio />} label="Изображение" />
              <FormControlLabel value="video" control={<Radio />} label="Видео" />
              <FormControlLabel value="gallery" control={<Radio />} label="Галерея (слайдер)" />
            </RadioGroup>
          </FormControl>

          {/* Загрузка в зависимости от типа */}
          {formData.mediaType === 'image' && (
            <Box sx={{ mt: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={(e) => handleFileUpload(e, 'image')}
                disabled={uploading}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'Загрузка...' : 'Загрузить изображение'}
                </Button>
              </label>
              {formData.image && (
                <Box sx={{ mt: 2 }}>
                  <img src={getFullImageSrc(formData.image)} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} />
                  <Button size="small" onClick={() => setFormData({ ...formData, image: '' })} sx={{ mt: 1 }}>
                    Удалить изображение
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {formData.mediaType === 'video' && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Способы добавления видео:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Загрузить файл на сервер (ограничение 500MB)<br/>
                  • Указать ссылку на YouTube/Vimeo<br/>
                  • Указать публичную ссылку на Яндекс.Диск
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  💡 Для Яндекс.Диска: откройте файл → "Поделиться" → "Скопировать публичную ссылку"
                </Typography>
              </Alert>
              <input
                accept="video/*"
                style={{ display: 'none' }}
                id="video-upload"
                type="file"
                onChange={(e) => handleFileUpload(e, 'videoUrl')}
                disabled={uploading}
              />
              <label htmlFor="video-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'Загрузка...' : 'Загрузить видео файл (макс 500MB)'}
                </Button>
              </label>
              <Typography sx={{ my: 1, textAlign: 'center' }}>или</Typography>
              <TextField
                fullWidth
                label="URL видео"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleChange}
                placeholder="https://disk.yandex.ru/... или https://youtube.com/..."
                helperText="Поддерживается: YouTube, Vimeo, Яндекс.Диск, прямые ссылки"
              />
              {formData.videoUrl && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Текущее видео:</strong><br/>
                    {formData.videoUrl}
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={() => setFormData({ ...formData, videoUrl: '' })} 
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Удалить видео
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {formData.mediaType === 'gallery' && (
            <Box sx={{ mt: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="gallery-upload"
                type="file"
                onChange={(e) => handleFileUpload(e, 'gallery', true)}
                disabled={uploading}
              />
              <label htmlFor="gallery-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'Загрузка...' : 'Добавить изображение в галерею'}
                </Button>
              </label>
              {formData.gallery && formData.gallery.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.gallery.map((img, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img src={getFullImageSrc(img)} alt={`Gallery ${index}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }} />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                        onClick={() => handleRemoveGalleryImage(index)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Загружено изображений: {formData.gallery?.length || 0}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Спикер"
            name="speakerName"
            value={formData.speakerName}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Количество мест"
            name="capacity"
            type="number"
            value={formData.capacity}
            onChange={handleChange}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.published}
                onChange={handleChange}
                name="published"
              />
            }
            label="Опубликовано"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={uploading || isHtmlEmpty(formData.description)}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsManager;
